import { createApi } from "@reduxjs/toolkit/query/react";
import { createCustomFetchBaseQuery } from "../baseQuery";

export const estimatorSettingApi = createApi({
  reducerPath: "estimator-setting",
  tagTypes: [
    "EstimatorSetting",
    "EstimatorServices",
    "AdditionalServices",
    "EstimatorTerms",
    "JobTypes",
    "Metrics",
    "GeographicAdjusters",
  ],
  baseQuery: createCustomFetchBaseQuery(),
  endpoints: (builder) => ({

    // =============================
    // GET ESTIMATOR SETTINGS
    // =============================
    getEstimatorSetting: builder.query({
      query: ({ type, page, limit , sortOrder , sortBy ,keyword , category } = {}) => {
        const params = new URLSearchParams();

        if (type) params.append("type", type);
        if (page) params.append("page", page);
        if (limit) params.append("limit", limit);
        if (sortOrder) params.append("sortOrder", sortOrder);
        if (sortBy) params.append("sortBy", sortBy);
        if (keyword) params.append("keyword", keyword);
        if (category) params.append("category", category);
        return {
          url: `contractor/estimator_setting?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (res) => res,
      providesTags: ["EstimatorSetting"],
    }),

    // =============================
    // SERVICES
    // =============================
    addServiceEstimatorSetting: builder.mutation({
      query: (body) => ({
        url: "contractor/estimator_setting/service",
        method: "POST",
        body,
      }),
      invalidatesTags: ["EstimatorServices"],
    }),

    editServiceEstimatorSetting: builder.mutation({
      query: ({ service_id, ...body }) => ({
        url: `contractor/estimator_setting/service/${service_id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["EstimatorServices"],
    }),

    deleteServiceEstimatorSetting: builder.mutation({
      query: (service_id) => ({
        url: `contractor/estimator_setting/service/${service_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["EstimatorServices"],
    }),

    restoreServicesEstimatorSetting: builder.mutation({
      query: (body) => ({
        url: "contractor/estimator_setting/service_restore",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["EstimatorServices"],
    }),

    // =============================
    // ADDITIONAL SERVICES
    // =============================
    addAdditionalServiceEstimatorSetting: builder.mutation({
      query: (body) => ({
        url: "contractor/estimator_setting/additional_service",
        method: "POST",
        body,
      }),
      invalidatesTags: ["AdditionalServices"],
    }),

    editAdditionalServiceEstimatorSetting: builder.mutation({
      query: ({ additional_service_id, ...body }) => ({
        url: `contractor/estimator_setting/additional_service/${additional_service_id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["AdditionalServices"],
    }),

    deleteAdditionalServiceEstimatorSetting: builder.mutation({
      query: (additional_service_id) => ({
        url: `contractor/estimator_setting/additional_service/${additional_service_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["AdditionalServices"],
    }),

    restoreAdditionalServicesEstimatorSetting: builder.mutation({
      query: (body) => ({
        url: "contractor/estimator_setting/additional_service_restore",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["AdditionalServices"],
    }),

    // =============================
    // ESTIMATOR TERMS
    // =============================
    addEstimatorTerm: builder.mutation({
      query: (body) => ({
        url: "contractor/estimator_setting/term",
        method: "POST",
        body,
      }),
      invalidatesTags: ["EstimatorTerms"],
    }),

    editEstimatorTerm: builder.mutation({
      query: ({ term_id, ...body }) => ({
        url: `contractor/estimator_setting/term/${term_id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["EstimatorTerms"],
    }),

    deleteEstimatorTerm: builder.mutation({
      query: (term_id) => ({
        url: `contractor/estimator_setting/term/${term_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["EstimatorTerms"],
    }),

    restoreEstimatorTerms: builder.mutation({
      query: (body) => ({
        url: "contractor/estimator_setting/term_restore",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["EstimatorTerms"],
    }),

    // =============================
    // JOB TYPES
    // =============================
    addJobTypeEstimatorSetting: builder.mutation({
      query: (body) => ({
        url: "contractor/estimator_setting/job_type",
        method: "POST",
        body,
      }),
      invalidatesTags: ["JobTypes"],
    }),

    editJobTypeEstimatorSetting: builder.mutation({
      query: (data) => ({
        url: `contractor/estimator_setting/job_type/${data.id}`,
        method: "PUT",
        body:data,
      }),
      invalidatesTags: ["JobTypes"],
    }),

    deleteJobTypeEstimatorSetting: builder.mutation({
      query: (job_type_id) => ({
        url: `contractor/estimator_setting/job_type/${job_type_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["JobTypes"],
    }),

    restoreJobTypesEstimatorSetting: builder.mutation({
      query: (body) => ({
        url: "contractor/estimator_setting/job_type_restore",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["JobTypes"],
    }),

    // =============================
    // METRICS
    // =============================
    addMetricEstimatorSetting: builder.mutation({
      query: (body) => ({
        url: "contractor/estimator_setting/metric",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Metrics"],
    }),

    editMetricEstimatorSetting: builder.mutation({
      query: ({ metric_id, ...body }) => ({
        url: `contractor/estimator_setting/metric/${metric_id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Metrics"],
    }),

    deleteMetricEstimatorSetting: builder.mutation({
      query: (metric_id) => ({
        url: `contractor/estimator_setting/metric/${metric_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Metrics"],
    }),

    restoreMetricsEstimatorSetting: builder.mutation({
      query: (body) => ({
        url: "contractor/estimator_setting/metric_restore",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Metrics"],
    }),

    // =============================
    // GEOGRAPHIC ADJUSTERS
    // =============================
    restoreGeographicAdjustersEstimatorSetting: builder.mutation({
      query: (body) => ({
        url: "contractor/estimator_setting/geographic_adjuster_restore",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["GeographicAdjusters"],
    }),

    // =============================
    // ASSIGN DEFAULT VALUES
    // =============================
    assignDefaultValuesEstimatorSetting: builder.mutation({
      query: ({data}) => ({
        url: "contractor/estimator_setting/assign_default_values",
        method: "PUT",
        body:data,
      }),
      invalidatesTags: ["EstimatorSetting"],
    }),


    restoreInvoiceNoteEstimatorSetting: builder.mutation({
      query: ({type}) => ({
        url: "contractor/estimator_setting/invoice_note_restore",
        method: "PUT",
        body: { type },
      }),
      invalidatesTags: ["EstimatorSetting"],
    }),


    editInvoiceNoteEstimatorSetting: builder.mutation({
      query: ({ note_id, data }) => ({
        url: `contractor/estimator_setting/invoice_note/${note_id}`,
        method: "PUT",
        body: data ,
      }),
      invalidatesTags: ["EstimatorSetting"],
    }),

    
    addInvoiceNoteEstimatorSetting: builder.mutation({
      query: (body) => ({
        url: "contractor/estimator_setting/invoice_note",
        method: "POST",
        body,
      }),
      invalidatesTags: ["EstimatorSetting"],
    }),

    deleteBulkTypesEstimatorSetting: builder.mutation({
      query: ({type , ids}) => ({
        url: `contractor/estimator_setting/delete/type`,
        method: "PUT",
        body:{type, ids},
      }),
      invalidatesTags: ["EstimatorSetting"],
    }),
    reorderEstimatorSettingItems : builder.mutation({
      query: (data) => ({
        url: `contractor/estimator_setting/reorder`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["EstimatorSetting"],
    })

  }),
});

export const {
  useGetEstimatorSettingQuery,

  useAddServiceEstimatorSettingMutation,
  useEditServiceEstimatorSettingMutation,
  useDeleteServiceEstimatorSettingMutation,
  useRestoreServicesEstimatorSettingMutation,

  useAddAdditionalServiceEstimatorSettingMutation,
  useEditAdditionalServiceEstimatorSettingMutation,
  useDeleteAdditionalServiceEstimatorSettingMutation,
  useRestoreAdditionalServicesEstimatorSettingMutation,

  useAddEstimatorTermMutation,
  useEditEstimatorTermMutation,
  useDeleteEstimatorTermMutation,
  useRestoreEstimatorTermsMutation,

  useAddJobTypeEstimatorSettingMutation,
  useEditJobTypeEstimatorSettingMutation,
  useDeleteJobTypeEstimatorSettingMutation,
  useRestoreJobTypesEstimatorSettingMutation,

  useAddMetricEstimatorSettingMutation,
  useEditMetricEstimatorSettingMutation,
  useDeleteMetricEstimatorSettingMutation,
  useRestoreMetricsEstimatorSettingMutation,

  useRestoreGeographicAdjustersEstimatorSettingMutation,
  useAssignDefaultValuesEstimatorSettingMutation,

  useRestoreInvoiceNoteEstimatorSettingMutation,
  useEditInvoiceNoteEstimatorSettingMutation, 
  useAddInvoiceNoteEstimatorSettingMutation,

  useDeleteBulkTypesEstimatorSettingMutation, 
  useReorderEstimatorSettingItemsMutation,
} = estimatorSettingApi;
